import { TableCell } from 'types/common/table';
import { getUtcUsFormatDate } from 'modules/utils/time';
import EditDateButton from 'components/backorder/table/currentPlanTable/EditDateButton';
import * as React from 'react';
import { toMantissaWithThousandComma } from 'modules/utils/number';
import { InfoIcon } from '@clovirtualfashion/connect-core';
import Tooltip from '@material-ui/core/Tooltip';
import { BackOrderHistory, PlanInformation } from 'types/api/backorder';
import DeleteBackOrderButton from 'components/backorder/table/backorderHistory/DeleteBackOrderButton';

//* plan information 내 current plan table
// [EN] Factory, not a constant: the End Date picker's floor is the group's current
//      measurement period end, which only the parent knows.
// [KO] 상수가 아니라 팩토리다. End Date 피커의 하한이 그룹의 현재 measurement period
//      종료일인데, 이 값은 부모만 알고 있다.
export const getCurrentPlanTableColumns = (
  measurementPeriodEndDate: string | null,
): TableCell<PlanInformation>[] => [
  {
    name: 'Plan Type',
    key: 'planType',
    align: 'center',
  },
  {
    name: 'Payment Method',
    key: 'paymentMethod',
    align: 'center',
    render: ({ paymentMethod }) => paymentMethod ?? '-',
  },
  {
    name: 'Order Date',
    key: 'orderDate',
    align: 'center',
    render: ({ orderDate }) => (orderDate ? getUtcUsFormatDate(orderDate) : '-'),
  },
  {
    name: (
      <>
        Subscription Term /<br />
        Start Date
      </>
    ),
    key: 'startDate',
    align: 'center',
    render: ({ subscriptionPeriod: { startDate } }) => {
      return startDate ? getUtcUsFormatDate(startDate) : '-';
    },
  },
  {
    name: (
      <>
        Subscription Term /<br />
        End Date
      </>
    ),
    key: 'endDate',
    align: 'center',
    render: data => {
      const {
        subscriptionId,
        canEndDateEdit,
        subscriptionPeriod: { endDate },
      } = data;
      return (
        // [EN] Dates inside the running measurement period are disabled: credit is
        //      settled per period and the API rejects a mid-period end date with a
        //      generic error (CLOSET-17946). Floor = period end date, or today (UTC)
        //      if that is later. Dates before the current end date remain selectable.
        // [KO] 진행 중인 measurement period 안의 날짜는 비활성화한다. 크레딧이 period
        //      단위로 정산되어 API 가 기간 중간 종료일을 모호한 에러로 거부하기 때문
        //      (CLOSET-17946). 하한 = period 종료일, 오늘(UTC) 이 더 늦으면 오늘.
        //      현재 end date 이전 날짜는 여전히 선택 가능하다.
        <EditDateButton
          subscriptionId={subscriptionId}
          type="end"
          targetDate={endDate}
          canEdit={canEndDateEdit}
          minDate={measurementPeriodEndDate}
        />
      );
    },
  },
  {
    name: 'Current Credit Balance',
    key: 'currentCreditBalance',
    align: 'center',
    render: ({ currentCreditBalance }) => toMantissaWithThousandComma(currentCreditBalance),
  },
  {
    name: 'CLO-SET Manager',
    key: 'backorderByEmail',
    align: 'center',
  },
];

export const currentPlanTableHeader: TableCell[] = getCurrentPlanTableColumns(null).map(
  column => {
    return {
      name: column.name,
      key: column.key,
    };
  },
);

//* back order history list table
export const backOrderHistoryTableColumns: TableCell<BackOrderHistory>[] = [
  {
    name: 'Plan Type',
    key: 'planType',
    align: 'left',
  },
  {
    name: 'Payment Method',
    key: 'paymentMethod',
    align: 'left',
    render: ({ paymentMethod }) => paymentMethod ?? '-',
  },
  {
    name: 'Order Date',
    key: 'orderDate',
    align: 'left',
    render: ({ orderDate }) => (orderDate ? getUtcUsFormatDate(orderDate) : '-'),
  },
  {
    name: (
      <>
        Subscription Term /<br />
        Start Date
      </>
    ),
    key: 'startDate',
    align: 'center',
    render: data => {
      const {
        subscriptionId,
        canStartDateEdit,
        subscriptionPeriod: { startDate },
      } = data;
      if (!canStartDateEdit) {
        return startDate ? getUtcUsFormatDate(startDate) : '-';
      }
      return (
        <EditDateButton
          subscriptionId={subscriptionId}
          type="start"
          targetDate={startDate}
          canEdit
        />
      );
    },
  },
  {
    name: (
      <>
        Subscription Term /<br />
        End Date
      </>
    ),
    key: 'endDate',
    align: 'center',
    render: data => {
      const {
        subscriptionId,
        canEndDateEdit,
        subscriptionPeriod: { endDate },
      } = data;
      if (!canEndDateEdit) {
        return endDate ? getUtcUsFormatDate(endDate) : '-';
      }
      return (
        <EditDateButton subscriptionId={subscriptionId} type="end" targetDate={endDate} canEdit />
      );
    },
  },
  {
    name: (
      <>
        Credit Balance of
        <br />
        the End Date
        <Tooltip
          title={
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontWeight: 'bolder' }}>Notice</div>
              <div>
                For an accurate True-up Fee calculation, please check Tableau to see if the group
                has overused Rendering Usage, Embed View, and API Call.
              </div>
            </div>
          }
          arrow
          placement="top"
        >
          <span>
            <InfoIcon size="12px" />
          </span>
        </Tooltip>
      </>
    ),
    key: 'creditBalance',
    align: 'center',
    render: ({ creditBalance }) =>
      creditBalance ? toMantissaWithThousandComma(creditBalance) : '-',
  },
  {
    name: 'CLO-SET Manager',
    key: 'backorderByEmail',
    align: 'center',
  },
  {
    name: 'Status',
    key: 'status',
    align: 'left',
    render: ({ subscriptionId, status, reservedCredit }) => {
      if (status === 'Reserved') {
        return (
          <DeleteBackOrderButton
            subscriptionId={subscriptionId}
            status={status}
            reservedCredit={reservedCredit}
          />
        );
      }
      return status;
    },
  },
];

export const backOrderHistoryTableHeader: TableCell[] = backOrderHistoryTableColumns.map(column => {
  return {
    name: column.name,
    key: column.key,
  };
});
